import React, { useEffect, useRef, useState } from "react";
import { ReceiptCategory } from "../services/receipt-category-service";
import {
  AccountingAccount,
  AccountingAccountService,
} from "../../accounting-account/services/accounting-account-service";
import { isSuccess } from "../../../models/response/base-response";
import { showToast } from "../../../components/custom/custom-toast";
interface ReceiptCategoryCreateProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: Partial<ReceiptCategory>) => void;
  item?: ReceiptCategory | null;
}

export const ReceiptCategoryCreate: React.FC<ReceiptCategoryCreateProps> = ({
  open,
  onClose,
  onSave,
  item,
}) => {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState<Partial<ReceiptCategory>>(
    item || {
      code: "",
      name: "",
      type: 1,
      description: "",
      debit_account: undefined,
      credit_account: undefined,
      vat_account: undefined,
    }
  );

  const [accountOptions, setAccountOptions] = useState<AccountingAccount[]>([]);
  const { fetch: fetchAccounts } = AccountingAccountService.list();
  const fetchAccountsRef = useRef(fetchAccounts);
  useEffect(() => {
    fetchAccountsRef.current = fetchAccounts;
  }, [fetchAccounts]);

  useEffect(() => {
    setForm(
      item || {
        code: "",
        name: "",
        type: 1,
        description: "",
        debit_account: undefined,
        credit_account: undefined,
        vat_account: undefined,
      }
    );
  }, [item]);

  useEffect(() => {
    const loadAccounts = async () => {
      if (!open) return;
      const res = await fetchAccountsRef.current({
        status: 1,
        type: 2,
        page: 1,
        limit: 1000,
      } as any);
      if (res && isSuccess(res)) { const d: any = res.data || {}; const list: AccountingAccount[] = Array.isArray(d.list) ? d.list : []; setAccountOptions(list); 
      } else { setAccountOptions([]); }
    };
    void loadAccounts();
  }, [open]);

  if (!open) return null;

  const handleSave = async () => {
    if (!form.code?.trim()) {
      showToast({ message: "Vui lòng nhập mã danh mục", type: "error" });
      return;
    }
  
    if (!form.name?.trim()) {
      showToast({ message: "Vui lòng nhập tên danh mục", type: "error" });
      return;
    }
  
    if (!form.credit_account || !form.debit_account || !form.vat_account) {
      showToast({ message: "Vui lòng chọn đầy đủ tài khoản Nợ và Có", type: "error" });
      return;
    }
  
    try {
      setLoading(true);
      await onSave(form); // nếu là async, chờ xử lý API từ parent
  
      onClose();
    } catch (err) {
      console.error(err);
      showToast({ message: "Đã xảy ra lỗi khi lưu danh mục", type: "error" });
    } finally {
      setLoading(false);
    }
  };
  

  return (
    <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-50 animate-fadeIn">
      <div className="bg-white rounded-2xl w-[540px] shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-green-500 text-white px-6 py-3 flex justify-between items-center">
          <h3 className="text-lg font-semibold">
            {item ? "Cập nhật danh mục Thu Chi" : "Thêm danh mục Thu Chi"}
          </h3>
          <button
            onClick={onClose}
            className="text-white text-2xl leading-none hover:opacity-80"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-4">
            {/* Code */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mã danh mục <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="VD: TC001"
                className="border border-gray-300 rounded-lg w-full px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                value={form.code ?? ""}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
              />
            </div>

            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tên danh mục <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="VD: Thu tiền khách hàng"
                className="border border-gray-300 rounded-lg w-full px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                value={form.name ?? ""}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>

            {/* Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Loại phiếu <span className="text-red-500">*</span>
              </label>
              <select
                className="border border-gray-300 rounded-lg w-full px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                value={form.type ?? 1}
                onChange={(e) =>
                  setForm({ ...form, type: Number(e.target.value) })
                }
              >
                <option value={1}>Phiếu Thu</option>
                <option value={2}>Phiếu Chi</option>
              </select>
            </div>

            {/* VAT */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tài khoản VAT
              </label>
              <select
                className="border border-gray-300 rounded-lg w-full px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                value={form.vat_account ?? ""}
                onChange={(e) =>
                  setForm({
                    ...form,
                    vat_account: e.target.value
                      ? Number(e.target.value)
                      : undefined,
                  })
                }
              >
                <option value="">-- Chọn tài khoản VAT --</option>
                {accountOptions.map((acc) => (
                  <option key={acc.id} value={acc.id}>
                    {acc.code} - {acc.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Credit account */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tài khoản Có <span className="text-red-500">*</span>
              </label>
              <select
                className="border border-gray-300 rounded-lg w-full px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                value={form.credit_account ?? ""}
                onChange={(e) =>
                  setForm({
                    ...form,
                    credit_account: e.target.value
                      ? Number(e.target.value)
                      : undefined,
                  })
                }
              >
                <option value="">-- Chọn tài khoản Có --</option>
                {accountOptions.map((acc) => (
                  <option key={acc.id} value={acc.id}>
                    {acc.code} - {acc.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Debit account */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tài khoản Nợ <span className="text-red-500">*</span>
              </label>
              <select
                className="border border-gray-300 rounded-lg w-full px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                value={form.debit_account ?? ""}
                onChange={(e) =>
                  setForm({
                    ...form,
                    debit_account: e.target.value
                      ? Number(e.target.value)
                      : undefined,
                  })
                }
              >
                <option value="">-- Chọn tài khoản Nợ --</option>
                {accountOptions.map((acc) => (
                  <option key={acc.id} value={acc.id}>
                    {acc.code} - {acc.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mô tả
            </label>
            <textarea
              placeholder="Mô tả ngắn gọn cho danh mục này..."
              className="border border-gray-300 rounded-lg w-full px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 resize-none h-24"
              value={form.description ?? ""}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
          >
            Hủy
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 rounded-lg bg-green-600 text-white font-medium hover:bg-green-700 transition"
          >
            Lưu
          </button>
        </div>
      </div>
    </div>
  );
};
